import * as yup from 'yup';

import { hasPreviousDamage } from '@/features/Contract/Property/utils';
import {
  previousInsuranceSchema,
  sharedContractBaseSchema,
} from '@/features/Contract/Shared/contractBaseSchema';
import {
  containsNonAsciiLetters,
  testSpecialChars,
} from '@/utils/strings/strings';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import { useInputStore } from '@/features/Sparten';

export const previousDamageSchema = yup.object({
  bestehenAnsprueche: yup.bool().default(false),
  memofeldAnsprueche: yup
    .string()
    .default('')
    .when({
      is: () => hasPreviousDamage.value,
      // Geht 1:1 an den Anbieter (SMIT). Zeichen ausserhalb von Latin-1 (ausser €) wie der
      // typografische Gedankenstrich lassen den Antrag dort scheitern (VGL-7272).
      // Nur bei sichtbarem Feld pruefen, sonst blockiert ein unsichtbarer Fehler den Antrag.
      then: yup.string().required().test(testSpecialChars),
    }),
  entschaedigungszahlung: yup.number().when({
    is: () => hasPreviousDamage.value,
    then: yup
      .number()
      .required()
      .nullable()
      .default(null)
      .integer('Bitte runden Sie den Wert.'),
    otherwise: yup.number().nullable().default(null),
  }),
  anzahl: yup
    .number()
    .nullable()
    .default(null)
    .when({
      is: () => hasPreviousDamage.value,
      then: (schema) =>
        schema.when(() => {
          const { vorschaden } = useInputStore();
          const hasMoreThanTwoDamages = getSimpleChoiceMeta(
            SIMPLE_CHOICE.VORSCHADEN,
            SIMPLE_CHOICE_META.VORSCHADEN_MORE_THAN_TWO_DAMAGES,
          ).includes(vorschaden);

          if (hasMoreThanTwoDamages) {
            return schema.required().min(3);
          }
          return schema.required().min(1).default(1);
        }),
    }),
});

export const propertyContractBaseSchema = sharedContractBaseSchema.concat(
  yup.object({
    antragAbgelehnt: yup.bool().default(false),
    emailBestaetigungKunde: yup.bool().default(false),
    memofeld: yup
      .string()
      .default('')
      .test(
        'isValidMemofeld',
        'Das Memo darf nur ASCII Zeichen enthalten.',
        (value) => !containsNonAsciiLetters(value),
      ),
    memofeldBeratungsdokumentation: yup
      .string()
      .default('')
      .test(
        'isValidMemofeld',
        'Das Feld darf nur ASCII Zeichen enthalten.',
        (value) => !containsNonAsciiLetters(value),
      ),
    vorversicherungsDaten: previousInsuranceSchema,
    vorschaden: previousDamageSchema,
  }),
);

export type PropertyPreviousDamage = yup.Asserts<typeof previousDamageSchema>;
export type PropertyContractBase = yup.Asserts<
  typeof propertyContractBaseSchema
>;
